import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, Clock, MessageSquare } from 'lucide-react';
import '../styles/Contact.css';
import { ContactHero } from '../components/PageHeroes';
import { sendEmail, ENDPOINTS } from '../utils/sendEmail';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const contactMethods = [
    { icon: Mail,    title: 'Email',          value: 'thomas@effectivestaff.co.uk',                              href: 'mailto:thomas@effectivestaff.co.uk', accent: 'blue' },
    { icon: Phone,   title: 'Phone',          value: '+44 744 630 1591',                                        href: 'tel:+447446301591',                 accent: 'teal' },
    { icon: MapPin,  title: 'Address',        value: 'Flat 11, 3rd Floor, 777 Old Kent Road, London, SE15 1NZ', href: '#',                                  accent: 'blue' },
    { icon: Clock,   title: 'Business Hours', value: 'Mon - Sat, 9am - 6pm',                                    href: '#',                                  accent: 'teal' }
  ];

  const faqs = [
    { q: 'What is your response time?',    a: 'We typically respond to all enquiries within 1 business hour during working hours.' },
    { q: 'Do you offer phone support?',    a: 'Yes, we offer phone support Monday to Friday, 9am to 6pm. Live chat is available 24/7.' },
    { q: 'How can I book a demo?',         a: 'Use the Request Demo button in the navigation, or mention it in your message and we will arrange a time.' },
    { q: 'Is there a free consultation?',  a: 'Yes — book a free 30-minute consultation with a specialist via our Book Consultation page.' }
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await sendEmail(ENDPOINTS.CONTACT, {
        name:    form.name,
        email:   form.email,
        phone:        form.phone || 'Not provided',
        subject:      form.subject,
        message:      form.message,
        to_email:     'thomas@effectivestaff.co.uk',
        reply_to:     form.email,
      });
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
  };
  const itemVariants = {
    hidden:  { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <motion.div className="contact-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <ContactHero />

      {/* ===== CONTACT METHODS ===== */}
      <section className="contact-section">
        <div className="contact-container">
          <motion.div className="contact-methods-grid" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {contactMethods.map((method, idx) => {
              const Icon = method.icon;
              return (
                <motion.a
                  key={idx}
                  href={method.href}
                  className={`contact-method-card accent-${method.accent}`}
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                >
                  <div className="contact-method-icon">
                    <Icon size={20} />
                  </div>
                  <h4>{method.title}</h4>
                  <p>{method.value}</p>
                </motion.a>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== FORM + SIDEBAR ===== */}
      <section className="contact-form-section">
        <div className="contact-container">
          <div className="contact-two-col">

            {/* Sidebar */}
            <motion.div className="contact-sidebar" initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="contact-eyebrow">Get in touch</span>
              <h2>We would love to hear from you</h2>
              <p>Whether you have a question, need a demo, or just want to know more about Effective Global Staff — our team is here and ready to help.</p>
              <div className="contact-sidebar-list">
                {[
                  'Payroll, invoicing, and accounting setup',
                  'HMRC and VAT compliance questions',
                  'Switching from another provider',
                  'Pricing and plan enquiries',
                  'Technical support',
                  'Partnership opportunities'
                ].map((item, idx) => (
                  <div key={idx} className="contact-sidebar-item">
                    <CheckCircle size={16} className="sidebar-check" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div className="contact-form-col" initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.08 }}>
              <div className="contact-form-card">
                <div className="contact-form-header">
                  <MessageSquare size={20} className="form-header-icon" />
                  <h3>Send us a message</h3>
                </div>

                {status === 'success' ? (
                  <div className="contact-success">
                    <CheckCircle size={44} className="success-icon" />
                    <h3>Message sent!</h3>
                    <p>Thanks for reaching out. We will get back to you within 1 business hour.</p>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Full name *</label>
                        <input name="name" value={form.name} onChange={handleChange} placeholder="Jane Smith" required />
                      </div>
                      <div className="form-group">
                        <label>Email address *</label>
                        <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="jane@company.co.uk" required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Phone number</label>
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="+44 7700 900000" />
                      </div>
                      <div className="form-group">
                        <label>Subject *</label>
                        <select name="subject" value={form.subject} onChange={handleChange} required>
                          <option value="">Select a topic</option>
                          <option value="General Enquiry">General Enquiry</option>
                          <option value="Platform Setup">Platform Setup</option>
                          <option value="Pricing & Plans">Pricing and Plans</option>
                          <option value="Technical Support">Technical Support</option>
                          <option value="HMRC Compliance">HMRC Compliance</option>
                          <option value="Partnership">Partnership</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Message *</label>
                      <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us how we can help..." rows={5} required />
                    </div>
                    {status === 'error' && (
                      <p className="form-error">Something went wrong. Please try again or call us directly.</p>
                    )}
                    <motion.button
                      type="submit"
                      className="contact-submit-btn"
                      disabled={status === 'sending'}
                      whileHover={{ scale: status === 'sending' ? 1 : 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {status === 'sending' ? 'Sending...' : (
                        <span className="submit-btn-content">
                          Send message <Send size={15} />
                        </span>
                      )}
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="contact-faq-section">
        <div className="contact-container">
          <motion.div className="contact-faq-header" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <span className="contact-eyebrow">FAQs</span>
            <h2>Common questions</h2>
            <p>Quick answers before you reach out.</p>
          </motion.div>
          <motion.div className="contact-faq-grid" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {faqs.map((faq, idx) => (
              <motion.div key={idx} className="contact-faq-card" variants={itemVariants} whileHover={{ y: -3 }}>
                <h4>{faq.q}</h4>
                <p>{faq.a}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;